import React, { useState, useEffect, useContext, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { Icon, Dropdown } from 'semantic-ui-react';

import { CopilotContext, CopilotProvider } from '../CopilotContext';
import Thematics, { buildThematicAnalysisPayload } from '../Functions/Thematics';
import ThematicCard from '../Components/ThematicCard';
import ThematicsCards from '../Components/ThematicsCards';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { SET_NEW_FILTERS } from "constants/actions";
import { ThematicIcon } from '../Home';
import HistoryButton from '../Components/HistoryButton';
import { Separator } from 'new_components/templates/ZeroStateScreen';
import Filters from '../Components/Filters';
// import { useDataSelectionGuard } from '../../NewAnalytics/DataSelectionGuard';

// Helper function to check if two selectedFactors match
const factorsMatch = (factor1, factor2) => {
  if (!factor1 || !factor2) return false;
  if (factor1.type !== factor2.type) return false;
  if (factor1.type === "Overview") return true;
  if (factor1.type === "culture") {
    return factor1.id === factor2.id && factor1.dimension === factor2.dimension;
  }
  if (factor1.type === "outcome") {
    return factor1.id === factor2.id;
  }
  return false;
};

function RiskHomeComponent({ goHome, initialSession, onSaveSession, openHistory, hasHistory = true, selected }) {
  const scrollRef = useRef(null);
  const endOfMessagesRef = useRef(null);
  const dispatch = useDispatch();

  // Guard to ensure data is selected, redirects to NewAnalytics if not
  // const { hasData } = useDataSelectionGuard();

  const core_data = useSelector((state) => state.audit?.core_data, shallowEqual);
  const raw_data = useSelector((state) => state.audit?.raw_data, shallowEqual);
  const getOrganizations = useSelector((state) => state.organizations);
  const getAuth = useSelector((state) => state.auth);
  const getSurveyQuestions = useSelector((state) => state.surveyquestions);

  // Get employee permissions for verbatim access
  const { employee, permissionRecords, employeeId } = useSelector(
    (state) => ({
      employee: state.employees?.userEmp,
      permissionRecords: state.employee_permission?.employee_permission,
      employeeId: state.auth?.employee_id,
    }),
    shallowEqual
  );

  // Check if user has verbatim comment access (read_feedback permission)
  const isAdmin = employee?.account_type === 5;
  const hasVerbatimAccess = isAdmin || employee?.read_feedback === 1;

  const {
    messages,
    setMessages,
    phase,
    clearMessages,
    setPromptText,
    getBatchEmbeddings,
  } = useContext(CopilotContext);

  /**
   * 1) If we have an initialSession with messages, we want to skip the intro
   *    even on the first render. We'll detect that synchronously.
   */
  const hasInitialMessages =
    initialSession?.messages && initialSession.messages.length > 0;

  /**
   * 2) Local states:
   *   - started: determines if we show the "intro" or the conversation.
   *   - prompt: stores {data} from your Thematics onClick
   */
  const [started, setStarted] = useState(hasInitialMessages);
  const [prompt, setPrompt] = useState("");
  const [selectedFactors, setSelectedFactors] = useState();
  const [followUp, setFollowUp] = useState(false);
  const [suggestionHistory, setSuggestionHistory] = useState([]);
  const [factorDropdown, setFactorDropdown] = useState([]);

  const selectedFilters = useSelector(
    (state) => state.audit?.new_filters,
    shallowEqual
  );

  useEffect(() => {
    return () => {
      dispatch({ type: SET_NEW_FILTERS, payload: [] });
    };
  }, []);

  /**
   * 3) On mount, if we do have an initialSession with messages, load them.
   *    Because we already used hasInitialMessages, if that was true, we set started=true.
   */
  useEffect(() => {
    if (initialSession) {
      // If there are messages, put them into the context:
      if (initialSession.messages?.length) {
        // Ensure all messages have selectedFactors if session has it
        const messagesWithFactors = initialSession.messages.map((msg) => {
          // If message doesn't have selectedFactors but session does, add it
          if (!msg.selectedFactors && initialSession.selectedFactors) {
            return { ...msg, selectedFactors: initialSession.selectedFactors };
          }
          return msg;
        });
        setMessages(messagesWithFactors);
      }
      // Also restore selectedFactors or suggestions if you saved them:
      if (initialSession.selectedFactors) {
        setSelectedFactors(initialSession.selectedFactors);
      }
      if (initialSession.suggestionHistory) {
        setSuggestionHistory(initialSession.suggestionHistory);
      }
      if (initialSession.filters) {
        dispatch({ type: SET_NEW_FILTERS, payload: initialSession.filters });
      }
    }
  }, [initialSession, setMessages, dispatch]);

  /**
   * 4) Original logic: if (phase === 'idle' && prompt) => setStarted + add function message
   */
  useEffect(() => {
    if (phase === "idle" && prompt) {
      console.log("Prompt set => adding function message", prompt);
      setStarted(true);
      setMessages((prev) => [
        ...prev,
        { role: "function", content: "Running thematic analysis" },
      ]);
    }
  }, [phase, prompt, setMessages]);

  /**
   * 5) Set up factor dropdown options
   */
  useEffect(() => {
    const factorDrop = [{ value: { type: "Overview" }, text: "Overview" }];
    const orgId = getAuth?.organization_id || getOrganizations?.organization?.id;
    const organizationData = getOrganizations?.[orgId];

    core_data?.questions?.dimensions?.forEach((q, d) => {
      q?.factors.forEach((f) => {
        factorDrop.push({
          text: f.title,
          value: { ...f, type: "culture", dimension: d },
        });
      });
    });

    if (organizationData) {
      const sortOrder = organizationData?.styling?.survey_sequence?.find(
        (item) => item.value === "outcome_question"
      )?.question?.sort_order;

      const outcomeQ = getSurveyQuestions?.outcome_questions?.find(
        (item) => item.sort_order === sortOrder
      );

      // Add outcome factors
      outcomeQ?.questions.forEach((q) => {
        factorDrop.push({
          text: q.name,
          value: { ...q, type: "outcome" },
        });
      });
    }

    setFactorDropdown(factorDrop);
    if (!selectedFactors && factorDrop[0]?.value) {
      setSelectedFactors(factorDrop[0]?.value);
    }
  }, [core_data, getAuth, getOrganizations, getSurveyQuestions, selectedFactors]);

  /**
   * 6) If followUp => add a follow_up message
   */
  useEffect(() => {
    if (followUp) {
      setMessages((prev) => [
        ...prev,
        { role: "follow_up", content: "Running thematic analysis on feedback" },
      ]);
    }
  }, [followUp, setMessages]);

  /**
   * 7) "registerHistory" => your original function to setPrompt({ data })
   */
  const registerHistory = (data) => {
    setPrompt({ data });
  };

  /**
   * 8) goBack => reset
   */
  const goBack = () => {
    clearMessages();
    setPromptText("");
    setStarted(false);
  };

  /**
   * 9) If not started + no messages => show the "intro"
   *    Because we used hasInitialMessages in `useState`, if we loaded a session
   *    with messages, `started` is already true => we skip this.
   */
  // Don't render until data check completes
  // if (!hasData) {
  //   return null;
  // }

  if (!started && messages.length === 0) {
    return (
      <Container>
        {/* <BackButton onClick={goHome}>
          <Icon style={{ marginRight: 10 }} name="arrow left" />
          Back
        </BackButton> */}
        <Thematics
          intro={true}
          selectedFactors={selectedFactors}
          setSelectedFactors={setSelectedFactors}
          onClick={({ data }) => registerHistory(data)}
          openHistory={openHistory}
          hasHistory={hasHistory}
        />
      </Container>
    );
  }

  // Handler for regenerating analysis
  const handleRegenerateAnalysis = () => {
    clearMessages();
    setFollowUp(false);
    setPrompt("");

    const { data, hasFeedback } = buildThematicAnalysisPayload({
      raw_data,
      selectedFilters,
      selectedFactors,
    });

    if (!hasFeedback) {
      setStarted(false);
      return;
    }

    setStarted(false);
    setPrompt({ data });
  };

  return (
    <Container ref={scrollRef}>
      {/* <BackButton onClick={goBack}>
        <Icon style={{ marginRight: 10 }} name="arrow left" />
        Back
      </BackButton>
       */}
      {/* Add a fixed white space to cover the background */}
      <div
        style={{
          height: "100%",
          position: "absolute",
          width: '100vw',
          backgroundColor: "white",
          top: -10,
          zIndex: -1,
          left: '-50%',
        }}
      />
      <div
        style={{
          height: "100%",
          position: "absolute",
          width: '100vw',
          backgroundColor: "white",
          top: -10,
          zIndex: -1,
        }}
      />
      <HeaderRow>
        <PageTitle>
          <TitleIconWrapper>
            <ThematicIcon />
          </TitleIconWrapper>
          Thematic Analysis
        </PageTitle>
        <ActionControls>
          <Dropdown
            selection
            placeholder="Select Category"
            value={selectedFactors}
            options={factorDropdown}
            onChange={(e, { value }) => setSelectedFactors(value)}
            style={{ minWidth: '200px' }}
          />
          <Filters hideSelected={true} />
          <RegenerateButton onClick={handleRegenerateAnalysis}>
            Run New Analysis
          </RegenerateButton>
          <Separator />
          <HistoryButton onClick={openHistory} disabled={!hasHistory} />
        </ActionControls>
      </HeaderRow>

      <Content>
        <MessageContainer>
          {messages.map((msg, i) => {
            // If function => ThematicCard
            if (msg.role === "function") {
              const hasAssistantAfter = messages
                .slice(i + 1)
                .some((m) => m.role === "assistant");

              return (
                <ThematicCard
                  key={i}
                  data={prompt.data} // same as original
                  getBatchEmbeddings={getBatchEmbeddings}
                  alreadyRun={hasAssistantAfter}
                  selectedFactors={msg.selectedFactors || selectedFactors}
                  onDone={(finalMessages) => {
                    onSaveSession?.({
                      type: "thematic",
                      messages: finalMessages,
                      selectedFactors,
                      suggestionHistory,
                      filters: selectedFilters,
                    });
                  }}
                />
              );
            }

            // Otherwise => ThematicsCards
            // Use the factor from the message if available, otherwise use current selectedFactors
            const messageFactor = msg.selectedFactors || selectedFactors;
            return (
              <ThematicsCards
                key={i}
                data={msg.content}
                factor={messageFactor}
                filters={selectedFilters}
                selected={messageFactor}
                hasVerbatimAccess={hasVerbatimAccess}
              />
            );
          })}
          <div ref={endOfMessagesRef} />
        </MessageContainer>
      </Content>
    </Container>
  );
}

export default function RiskHome(props) {
  return (
    <CopilotProvider>
      <RiskHomeComponent {...props} />
    </CopilotProvider>
  );
}

/* ====================== STYLES ====================== */

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div`
  font-family: "Raleway", sans-serif;
  padding: 20px;
  padding-top: 0px;
  width: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 30px;
`;

const PageTitle = styled.div`
  font-size: 28px;
  font-weight: bold;
  color: #2A3039;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TitleIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background: #F8F9FA;
  border-radius: 7px;
  color: #1F2937;
`;

const ActionControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  > * {
    display: flex;
    align-items: center;
    margin: 0 !important;
  }
  
  button {
    margin: 0 !important;
  }
`;

const RegenerateButton = styled.button`
  padding: 10px 20px;
  background-color: #2d70e2;
  color: white;
  cursor: pointer;
  transition: 0.3s;
  border-radius: 8px;
  display: inline-block;
  font-size: 14px;
  font-weight: 500;
  border: none;
  font-family: "Raleway";

  &:hover {
    background-color: #1c4db8;
  }
`;

const Content = styled.div`
  display:flex;
  flex-direction: row;
  justify-content: center;
  margin-top:20px;
  width:100%;
`;

const MessageContainer = styled.div`
  width:100%;
  padding-bottom:150px;
`;
